import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from './theme/ThemeProvider';
import { LoginApp } from './LoginApp';
import { SuperPickerApp } from './super-picker/SuperPickerApp';
import { SuperPickerPublicApp } from './super-picker-public/SuperPickerPublicApp';
import UnauthorizedEntryPage from './super-picker/pages/UnauthorizedEntryPage';
import { SuperCalculatorApp } from './super-calculator/SuperCalculatorApp';
import { SuperConnectorApp } from './super-connector/SuperConnectorApp';
import { ProjectDeckApp } from './project-deck/ProjectDeckApp';
import { SpecialPromoApp } from './project-deck/SpecialPromoApp';
import { ProjectDeckAccessGate } from './project-deck/components/ProjectDeckAccessGate';
import { CommunicationApp } from './communication/CommunicationApp';
import { ProjectEssentialApp } from './project-essential/ProjectEssentialApp';
import { SuperPersonalizedPsfApp } from './super-personalized-psf/SuperPersonalizedPsfApp';
import PublicPersonalizedPsfApp from './super-personalized-psf/PublicPersonalizedPsfApp';
import { DevLoginPage } from './DevLoginPage';
import { ExternalLoginPage } from './super-picker/pages/ExternalLoginPage';
import TowerViewPublicApp from './tower-view-public/TowerViewPublicApp';
import DataDashboardApp from './data-dashboard/DataDashboardApp';
import { DeckComingSoon, getDeckByPath } from './project-deck/components/DeckShell';

// Consumer-facing 3D map share; split into its own chunk (the other apps are
// still imported eagerly above, so this only keeps the share code out of theirs).
const SharePublicApp = React.lazy(() => import('./share-public/SharePublicApp'));

function SharePublicRoute() {
  return (
    <React.Suspense fallback={<div style={{ position: 'fixed', inset: 0, background: '#050c17' }} />}>
      <SharePublicApp />
    </React.Suspense>
  );
}

function ComingSoonDeckRoute() {
  const location = useLocation();
  return <DeckComingSoon deck={getDeckByPath(location.pathname)} />;
}

/**
 * Root app shell. Routes by path so multiple modules can be mounted:
 * - /sso → LoginApp (SSO callback, exchanges token and redirects)
 * - /super-picker → Super Picker dashboard
 * - /unauthorized → Unauthorized page
 * - / → redirect to /super-picker (JWT redirect could be used later for calculator, crm, etc.)
 *
 * Theme is stored per module (app-theme-super-picker, app-theme-super-calculator) so each
 * module can have its own theme preference.
 */
function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <Routes>
        <Route path="/unauthorized" element={<UnauthorizedEntryPage />} />
        <Route path="/dev-login" element={<DevLoginPage />} />
        <Route path="/sso" element={<LoginApp />} />
        <Route path="/super-picker/external/login" element={<ExternalLoginPage />} />
        <Route path="/tower-view-public" element={<TowerViewPublicApp />} />
        <Route path="/share/:key" element={<SharePublicRoute />} />
        <Route path="/super-picker" element={<SuperPickerApp />} />
        <Route path="/super-picker/*" element={<SuperPickerApp />} />
        <Route path="/super-picker-public" element={<SuperPickerPublicApp />} />
        <Route path="/super-picker-public/*" element={<SuperPickerPublicApp />} />
        <Route path="/super-calculator" element={<SuperCalculatorApp />} />
        <Route path="/super-calculator/*" element={<SuperCalculatorApp />} />
        <Route path="/super-connector" element={<SuperConnectorApp />} />
        <Route path="/super-connector/*" element={<SuperConnectorApp />} />
        <Route path="/project-deck" element={<ProjectDeckAccessGate><ProjectDeckApp /></ProjectDeckAccessGate>} />
        <Route path="/project-deck/project-pipeline/*" element={<ProjectDeckAccessGate><ProjectDeckApp /></ProjectDeckAccessGate>} />
        <Route path="/project-deck/special-promo" element={<ProjectDeckAccessGate><SpecialPromoApp /></ProjectDeckAccessGate>} />
        <Route path="/project-deck/affordable-projects" element={<ProjectDeckAccessGate><ComingSoonDeckRoute /></ProjectDeckAccessGate>} />
        <Route path="/project-deck/large-floor-plate" element={<ProjectDeckAccessGate><ComingSoonDeckRoute /></ProjectDeckAccessGate>} />
        <Route path="/project-deck/top-projects" element={<ProjectDeckAccessGate><ComingSoonDeckRoute /></ProjectDeckAccessGate>} />
        <Route path="/project-deck/gls-enbloc-upcoming" element={<ProjectDeckAccessGate><ComingSoonDeckRoute /></ProjectDeckAccessGate>} />
        <Route path="/project-deck/gls-enbloc-current" element={<ProjectDeckAccessGate><ComingSoonDeckRoute /></ProjectDeckAccessGate>} />
        <Route path="/project-deck/available-units" element={<ProjectDeckAccessGate><ComingSoonDeckRoute /></ProjectDeckAccessGate>} />
        <Route path="/project-deck/*" element={<Navigate to="/project-deck" replace />} />
        <Route path="/communication" element={<CommunicationApp />} />
        <Route path="/communication/*" element={<CommunicationApp />} />
        <Route path="/project-essential" element={<ProjectEssentialApp />} />
        <Route path="/project-essential/*" element={<ProjectEssentialApp />} />
        <Route path="/super-personalized-psf" element={<SuperPersonalizedPsfApp />} />
        <Route path="/super-personalized-psf/*" element={<SuperPersonalizedPsfApp />} />
        <Route path="/super-personalized-psf-public" element={<PublicPersonalizedPsfApp />} />
        <Route path="/data-dashboard" element={<DataDashboardApp />} />
        <Route path="/purchase-planner" element={<SuperCalculatorApp />} />
        <Route path="/mortgage-loan" element={<Navigate to="/super-calculator/affordability" replace />} />
        <Route path="/profile-manager" element={<SuperCalculatorApp />} />
        <Route path="/" element={<Navigate to="/super-picker" replace />} />
        <Route path="*" element={<Navigate to="/super-picker" replace />} />
        </Routes>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
